package com.ijoin.data.api

import com.ijoin.data.repository.AuthRepository
import com.ijoin.data.storage.TokenManager
import com.ijoin.i18n.currentLanguageCode
import io.ktor.client.*
import io.ktor.client.call.*
import io.ktor.client.plugins.contentnegotiation.*
import io.ktor.client.plugins.logging.*
import io.ktor.client.request.*
import io.ktor.client.statement.*
import io.ktor.http.*
import io.ktor.serialization.kotlinx.json.*
import kotlinx.coroutines.sync.Mutex
import kotlinx.coroutines.sync.withLock
import kotlinx.serialization.json.Json

    private const val DEFAULT_BASE_URL = "https://ijoin.info"//prod
//    private const val DEFAULT_BASE_URL = "http://192.168.1.70:8080"//Android
//    private const val DEFAULT_BASE_URL = "http://192.168.1.83:8080"//Android
//    private const val DEFAULT_BASE_URL = "http://10.0.2.2:8080"//Android
//    private const val DEFAULT_BASE_URL = "http://localhost:8080"//web

object ApiClient {

    private val tokenManager = TokenManager()
    private val authRepository = AuthRepository()
    private val refreshMutex = Mutex()
    private var isRefreshing = false
    private var overrideBaseUrl: String? = null
    var onTokenExpired: (() -> Unit)? = null

    val client = HttpClient {
        install(ContentNegotiation) {
            json(Json {
                prettyPrint = true
                isLenient = true
                ignoreUnknownKeys = true
            })
        }

        install(Logging) {
            logger = Logger.DEFAULT
            level = LogLevel.INFO
        }

    }

    fun getBaseUrl() = overrideBaseUrl?.takeIf { it.isNotBlank() } ?: DEFAULT_BASE_URL

    fun overrideBaseUrlForTests(baseUrl: String) {
        overrideBaseUrl = baseUrl
    }

    fun clearBaseUrlOverride() {
        overrideBaseUrl = null
    }

    // Extension function to add auth token and language to requests
    fun HttpRequestBuilder.addAuthToken() {
        val token = tokenManager.getToken()
        if (token != null) {
            header(HttpHeaders.Authorization, "Bearer $token")
        }
        header(HttpHeaders.AcceptLanguage, currentLanguageCode())
    }

    // Extension function to add only language header to requests (for public endpoints)
    fun HttpRequestBuilder.addLanguageHeader() {
        header(HttpHeaders.AcceptLanguage, currentLanguageCode())
    }

    /**
     * Executes a request with automatic token refresh on 403 errors
     */
    suspend fun <T> executeWithTokenRefresh(
        request: suspend () -> T
    ): T {
        return try {
            val result = request()
            // If result is HttpResponse, check status directly
            if (result is HttpResponse && (result.status == HttpStatusCode.Forbidden || result.status == HttpStatusCode.Unauthorized)) {
                // Handle auth failures by trying to refresh token
                return handleAuthError(result.status) { request() }
            }
            result
        } catch (e: Exception) {
            // Check if it's a 403 error by examining the exception message
            // This approach works across all platforms (Android, iOS, JS, WasmJS)
            val message = e.message ?: ""
            val isForbidden = message.contains("403") ||
                message.contains("Forbidden") ||
                message.contains("HTTP 403")
            val isUnauthorized = message.contains("401") ||
                message.contains("Unauthorized") ||
                message.contains("HTTP 401")

            if (isForbidden || isUnauthorized) {
                val status = if (isUnauthorized) HttpStatusCode.Unauthorized else HttpStatusCode.Forbidden
                return handleAuthError(status) { request() }
            } else {
                throw e
            }
        }
    }

    private suspend fun <T> handleAuthError(
        status: HttpStatusCode,
        request: suspend () -> T
    ): T {
        // Try to refresh token. If another coroutine is already refreshing, wait for it.
        val refreshResult = refreshMutex.withLock {
            if (isRefreshing) {
                return@withLock null
            }
            isRefreshing = true
            try {
                authRepository.refreshToken()
            } finally {
                isRefreshing = false
            }
        }

        // If another request was already refreshing, retry with the latest credentials without forcing logout.
        if (refreshResult == null) {
            val retried = request()
            if (retried is HttpResponse && retried.status == status) {
                throw ApiException(retried.status, "Request is ${retried.status.description}")
            }
            return retried
        }

        if (refreshResult.isSuccess) {
            // Retry the original request with new token
            val retryResult = request()
            if (retryResult is HttpResponse && retryResult.status == status) {
                // The backend explicitly forbids this action even with a valid token – propagate the error.
                throw ApiException(retryResult.status, "Request is ${retryResult.status.description}")
            }
            return retryResult
        } else {
            // Refresh failed – clear credentials and notify listeners so the UI can navigate to login.
            tokenManager.clearToken()
            tokenManager.clearRefreshToken()
            onTokenExpired?.invoke()

            val failure = refreshResult.exceptionOrNull()
            if (failure is ApiException) {
                throw failure
            }
            throw failure ?: ApiException(status, "Authentication expired. Please login again.")
        }
    }
}
